import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { ModalWrapper, SectionTitle } from 'modules/shared/components';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { logError } from 'utils/logger';
import { AttendanceEdit, PayrollRow, updateAttendance } from '../../hr.api';
import { money, monthLabel } from '../payroll.utils';

/** The numeric fields HR may correct, in the order they read on the register. */
const FIELDS: Array<{ key: keyof AttendanceEdit; label: string; help?: string }> = [
  { key: 'total_days', label: 'Scheduled days', help: 'Working days in the month. Lower it for a public holiday.' },
  { key: 'working_days', label: 'Days attended' },
  { key: 'working_hours', label: 'Hours worked' },
  { key: 'overtime_hours', label: 'Overtime hours' },
  { key: 'negative_hours', label: 'Negative hours', help: 'Subtracted from payable hours.' },
  { key: 'null_hours', label: 'Null hours' },
  { key: 'incentive', label: 'Incentive (₹)' },
];

const toForm = (row: PayrollRow | null) => ({
  total_days: row?.total_days ?? 0,
  working_days: row?.working_days ?? 0,
  working_hours: row?.working_hours ?? 0,
  overtime_hours: row?.overtime_hours ?? 0,
  negative_hours: row?.negative_hours ?? 0,
  null_hours: row?.null_hours ?? 0,
  incentive: row?.incentive ?? 0,
});

/**
 * Corrects one payroll row.
 *
 * Only the inputs are editable — pay is never typed in. The server re-prices the row from the
 * hours on save and answers with the new figures, which is why the preview below is the
 * response and not a second copy of the formula living in the browser.
 */
export default function PayrollRowEditModal({
  open,
  row,
  onClose,
  onSaved,
}: {
  open: boolean;
  row: PayrollRow | null;
  onClose: () => void;
  onSaved: (updated: PayrollRow) => void;
}) {
  const [form, setForm] = useState(toForm(row));
  const [verified, setVerified] = useState(Boolean(row?.verified_by_hr));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm(toForm(row));
    setVerified(Boolean(row?.verified_by_hr));
  }, [row]);

  // Shown as guidance while editing; the authoritative figure comes back from the save.
  const previewHours = useMemo(
    () =>
      Number(form.working_hours) +
      Number(form.overtime_hours) +
      Number(form.null_hours) -
      Number(form.negative_hours) +
      Number(row?.paid_leave_days ?? 0) * 8.5,
    [form, row]
  );

  const handleSave = async () => {
    if (!row) return;
    setSaving(true);
    try {
      const updated = await updateAttendance(row.employee_id, row.id, {
        ...form,
        verified_by_hr: verified,
      });
      toast.success('Attendance updated');
      onSaved(updated);
      onClose();
    } catch (err) {
      toast.error(
        logError({ error: err, message: 'Failed to update attendance', code: 'ATTENDANCE_UPDATE_FAILED' })
      );
    } finally {
      setSaving(false);
    }
  };

  if (!row) return null;

  return (
    <ModalWrapper open={open} onClose={onClose} maxWidth='sm'>
      <SectionTitle title={row.employee_name ?? 'Employee'} />
      <Typography variant='body2' color='text.secondary'>
        {[row.employee_code, row.designation, row.department].filter(Boolean).join(' · ')} —{' '}
        {monthLabel(row.year, row.month)}
      </Typography>

      <Stack gap={2} sx={{ overflowY: 'auto' }}>
        {FIELDS.map(({ key, label, help }) => (
          <TextField
            key={String(key)}
            size='small'
            type='number'
            label={label}
            helperText={help}
            value={form[key as keyof typeof form]}
            onChange={(e) => setForm((prev) => ({ ...prev, [key]: e.target.value }))}
            inputProps={{ min: 0, step: key === 'incentive' || key === 'total_days' || key === 'working_days' ? 1 : 0.5 }}
          />
        ))}

        <FormControlLabel
          control={<Switch checked={verified} onChange={(e) => setVerified(e.target.checked)} />}
          label='Verified by HR'
        />

        {Number(row.paid_leave_days) > 0 && (
          <Alert severity='info'>
            {row.paid_leave_days} day(s) of approved paid leave are added to payable hours. Unpaid
            leave ({row.unpaid_leave_days}) is not paid and is not deducted twice.
          </Alert>
        )}

        <Divider />

        <Stack direction='row' justifyContent='space-between'>
          <Typography variant='body2' color='text.secondary'>
            Payable hours (preview)
          </Typography>
          <Typography variant='body2'>{previewHours.toFixed(1)}</Typography>
        </Stack>
        <Stack direction='row' justifyContent='space-between'>
          <Typography variant='body2' color='text.secondary'>
            Rate per hour
          </Typography>
          <Typography variant='body2'>{money(row.per_hour_rate)}</Typography>
        </Stack>
        <Stack direction='row' justifyContent='space-between'>
          <Typography variant='body2' color='text.secondary'>
            Net payable (last calculated)
          </Typography>
          <Typography variant='body2' fontWeight={600}>
            {money(row.net_payable)}
          </Typography>
        </Stack>
      </Stack>

      <Stack direction='row' gap={2} justifyContent='flex-end'>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant='contained' onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </Stack>
    </ModalWrapper>
  );
}
